import {
  convertEventSelectToTarget,
  DataTable,
  formatDataToLabelValue,
  FormFeedBack,
  ModalConfirmAction,
  PageWrapper,
  PROFILE_URL_PATH,
  Select,
  STATUSPAGE,
} from '@repo/ui';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useFormik } from 'formik';
import { useAuth } from 'presentation/app/hooks/useAuth';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, Col, Form, Label, Row } from 'reactstrap';
import * as Yup from 'yup';

import { makeColumnsProfessionalsLists } from '../../../common/columns/columns-professionals-lists';
import { useProfessionalsLists } from '../../../hooks/useProfessionalsLists';

export const AssistantsList = () => {
  // Hooks
  const navigate = useNavigate();
  const { getLegends } = useLegends();
  const { currentUser } = useAuth();
  const { coursesUser } = useAppSelector((state) => state.courses);
  const { organizationsUser } = useAppSelector((state) => state.organizations);
  const {
    isUnlinkModalOpen,
    selectedProfessionalId,
    unlinkModalButtonsStatusPage,
    listStatusPage,
    professionalsData,
    paginationMetadata,
    debouncedRequestGetAllProfessionals,
    toggleUnlinkModal,
    setSelectedProfessionalId,
    requestUnlinkProfessional,
    handlePageChange,
    handleSortRemote,
    setListStatusPage,
    handleSearchRemote,
    removeProfessionalsData,
    handleRowsPerPageChange,
    requestGetAllCoursesUser,
    requestGetAllOrganizationsUser,
  } = useProfessionalsLists();

  const validation = useFormik({
    initialValues: {
      entity: null,
      course: null,
    },
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
    }),

    // There is no onSubmit function on this form (no submit button).
    // The API request is sent everytime any filter changes, so it is controlled via useEffect and DataTable's remote pagination.
    onSubmit: () => {
      return;
    },
  });

  // useEffect responsible for calling API and fetching DataTable's data
  useEffect(() => {
    const valuesToSubmit = {
      entity: validation.values?.entity?.value,
      course: validation.values?.course?.value,
      page: paginationMetadata.selectedPage.toString(),
      length: paginationMetadata.rowsPerPage.toString(),
      order: paginationMetadata.sortDirection,
      orderColumn: paginationMetadata.orderColumn,
      query: paginationMetadata.searchString,
      profileType: PROFILE_TYPE.ASSISTANT,
    };
    setListStatusPage(STATUSPAGE.LOADING);

    debouncedRequestGetAllProfessionals(valuesToSubmit);

    return () => {
      removeProfessionalsData();
    };
  }, [
    validation.values?.entity,
    validation.values?.course,
    paginationMetadata,
  ]);

  useEffect(() => {
    requestGetAllCoursesUser();
    requestGetAllOrganizationsUser();
  }, []);

  // Constants
  const noDataConfig = {
    noDataTitle: 'Nenhum assistente encontrado',
    noDataDescription:
      'Use os filtros acima para pesquisar assistentes por nome, organização ou curso.',
  };

  return (
    <PageWrapper title="Assistentes">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Assistentes</h4>

        {/* {currentUser?.activeProfile?.profileId !== PROFILE_TYPE.DIRECTOR && (
          <button
            type="submit"
            className="btn btn-primary waves-effect waves-light d-flex align-items-center gap-1"
            onClick={() => {
              navigate(`/pre-cadastro/${PROFILE_URL_PATH.ASSISTANTS}`);
            }}
          >
            <i className="ri-add-line" style={{ fontSize: '1rem' }}></i>
            Novo Assistente
          </button>
        )} */}
      </div>
      <Card>
        <Form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <CardBody>
            <Row>
              <>
                <Col lg={6} className="mb-3">
                  <Label htmlFor="entity" className="form-label">
                    {`${getLegends('ORGANIZACAO', false)}:`}
                  </Label>
                  <Select
                    isClearable
                    id="entity"
                    name="entity"
                    placeholder={`Todas as ${getLegends('ORGANIZACAO', true)}`}
                    value={validation.values.entity}
                    onChange={(e) => {
                      requestGetAllCoursesUser(e?.value || '');
                      validation.setValues({
                        ...validation.values,
                        course: null,
                      });

                      validation.handleChange(
                        convertEventSelectToTarget('entity', e),
                      );
                    }}
                    invalid={
                      !!(validation.touched && validation.errors?.entity)
                    }
                    onBlur={validation.handleBlur}
                    options={formatDataToLabelValue(
                      organizationsUser as never,
                      'id',
                      'nome',
                    )}
                  />
                  <FormFeedBack
                    error={String(validation.errors?.entity)}
                    isInvalid={
                      !!(validation.touched && validation.errors?.entity)
                    }
                  />
                </Col>

                <Col lg={6} className="mb-3">
                  <Label htmlFor="course" className="form-label">
                    {`${getLegends('CURSO', false)}:`}
                  </Label>
                  <Select
                    isClearable
                    id="course"
                    name="course"
                    placeholder={`Todos os ${getLegends('CURSO', true)}`}
                    value={validation.values.course}
                    onChange={(e) => {
                      validation.handleChange(
                        convertEventSelectToTarget('course', e),
                      );
                    }}
                    onBlur={validation.handleBlur}
                    options={formatDataToLabelValue(
                      coursesUser as never,
                      'id',
                      'nome',
                    )}
                  />
                  <FormFeedBack
                    error={String(validation.errors?.course)}
                    isInvalid={
                      !!(validation.touched && validation.errors?.course)
                    }
                  />
                </Col>
              </>
            </Row>
          </CardBody>
        </Form>

        <CardBody>
          <DataTable
            noDataTitle={noDataConfig.noDataTitle}
            noDataDescription={noDataConfig.noDataDescription}
            actions={[
              {
                id: 1,
                name: 'overview',
                label: 'Visão Geral',
                onPress: (cellProps) => {
                  navigate({
                    pathname: `/gerenciamento-de-profissional/${PROFILE_URL_PATH.ASSISTANTS}/${cellProps.id}`,
                    search: '?painel=visao-geral',
                  });
                },
                icon: 'ri-line-chart-line',
                classNameColorIcon: 'primary',
              },
              {
                id: 2,
                name: 'view',
                label: 'Visualizar',
                onPress: (cellProps) => {
                  navigate({
                    pathname: `/gerenciamento-de-profissional/${PROFILE_URL_PATH.ASSISTANTS}/${cellProps.id}`,
                    search: '?painel=dados-cadastrais',
                  });
                },
                icon: 'ri-file-search-line',
                classNameColorIcon: 'primary',
              },
              // {
              //   id: 2,
              //   name: 'unlink',
              //   label: 'Desvincular',
              //   icon: 'ri-indeterminate-circle-fill',
              //   classNameColorIcon: 'danger',
              //   onPress: (cellProps) => {
              //     setSelectedProfessionalId(cellProps.id);
              //     toggleUnlinkModal();
              //   },
              // },
              // {
              //   id: 3,
              //   name: 'edit',
              //   label: 'Editar',
              //   onPress: (cellProps) => {
              //     navigate(
              //       `/gerenciamento-de-profissional/${PROFILE_URL_PATH.ASSISTANTS}/editar/${cellProps.id}`, - to change in privateRoutes later
              //     );
              //   },
              //   icon: 'ri-edit-line',
              //   classNameColorIcon: 'primary',
              // },
            ]}
            canSearch
            searchServer
            onSearch={handleSearchRemote}
            columns={makeColumnsProfessionalsLists()}
            data={professionalsData?.data || []}
            loading={Boolean(listStatusPage)}
            pagination
            paginationServer
            paginationTotalRows={professionalsData?.total}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
            sortServer
            onSort={handleSortRemote}
          />
        </CardBody>
      </Card>

      <ModalConfirmAction
        modalTheme="danger"
        title="Deseja desvincular?"
        isOpen={isUnlinkModalOpen}
        toggle={toggleUnlinkModal}
        onConfirm={() =>
          requestUnlinkProfessional(
            { id: selectedProfessionalId },
            PROFILE_TYPE.ASSISTANT,
          )
        }
        loading={unlinkModalButtonsStatusPage === STATUSPAGE.LOADING}
      />
    </PageWrapper>
  );
};
